// ==========================================
// Browser session: access token, refresh, sign-out
// ==========================================
//
// The access token lives in memory only (`useAuthStore().token`). The refresh token never reaches page
// script: the server keeps it in the httpOnly `cf_refresh` cookie (path /api/v1/auth, SameSite=Strict) and
// rotates it on every refresh. Both HTTP stacks (the generated SDK's interceptor and the legacy
// `client.ts`) call `refreshSession()` / `clearAuth()` from here (QG-CLI-COMP-001).
import { refreshToken as refreshAccessToken } from '../../generated/api/sdk.gen.js';
import { queryClient } from '../../lib/query-client.js';
import { useAuthStore } from '../../store/index.js';

/**
 * The API's origin. A production build without `VITE_API_URL` talks to the origin that served it (nginx routes
 * `/api` on app.conflui.com and dev.app.conflui.com to that environment's server), so one client image serves every
 * environment. It must stay absolute: `new URL(API_V1_BASE)` builds the conversation socket and
 * `new URL(…, API_BASE_URL)` the code previews. The dev server keeps the local API.
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD && typeof window !== 'undefined' ? window.location.origin : 'http://localhost:4100');

// Current API version prefix — keep in sync with server's API_VERSION_PREFIX.
export const API_VERSION_PREFIX = '/api/v1';

// Fully-qualified base for callers that need it for direct fetch (e.g. SSE streams).
export const API_V1_BASE = `${API_BASE_URL}${API_VERSION_PREFIX}`;

/** Storage written by this session that belongs to the signed-in identity, removed on sign-out. */
const IDENTITY_STORAGE_PREFIXES = ['conv-last-event-', 'workflow-draft-', 'entity-draft:'];

const AUTH_CHANNEL = 'confluent-auth';
const REFRESH_LOCK = 'confluent-auth-refresh';

/** The current access token, or null before the session has been restored. */
export function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

function removeIdentityStorage(): void {
  if (typeof localStorage === 'undefined') return;
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (key && IDENTITY_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) localStorage.removeItem(key);
  }
}

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_CHANNEL);

/** Forget the identity in this tab: credentials, cached data and identity-scoped storage. */
function signOutLocally(): void {
  useAuthStore.getState().logout();
  queryClient.clear();
  removeIdentityStorage();
}

// Another tab signed out: follow it. `AuthGate` (App.tsx) sees `isAuthenticated` go false and routes to /login.
channel?.addEventListener('message', (event: MessageEvent<unknown>) => {
  if (event.data === 'signed-out') signOutLocally();
});

/**
 * Ends the session in this tab and every other open tab. Call when the person signs out, or when a refresh
 * has genuinely failed — never on a bare 401.
 */
export function clearAuth(): void {
  signOutLocally();
  channel?.postMessage('signed-out');
}

/**
 * The server could not be reached or failed (network error, 5xx) while refreshing: the session itself may
 * still be valid, so callers keep it and retry instead of signing the person out.
 */
export class SessionUnavailableError extends Error {
  constructor() {
    super('The server could not be reached.');
    this.name = 'SessionUnavailableError';
  }
}

/** Sentinel that serializes concurrent 401s in this tab — from either HTTP stack — into one refresh. */
let refreshPromise: Promise<{ token: string } | null> | null = null;

/**
 * Exchanges the session cookie for a new access token.
 *
 * - Resolves `{ token }` on success.
 * - Resolves `null` when the server refuses the session (401/403: no cookie, expired, revoked) — callers
 *   then sign out.
 * - Rejects with `SessionUnavailableError` when the server cannot answer (network error, 5xx) — callers keep
 *   the session and retry; a server restart must not sign anyone out.
 *
 * Refreshes are serialized across tabs with the Web Locks API: the server rotates the refresh token on
 * every use and treats a reused one as theft (revoking every session), so two tabs must never present the
 * same cookie at once. The second tab's request runs after the first's response has replaced the cookie.
 */
export function refreshSession(): Promise<{ token: string } | null> {
  if (!refreshPromise) {
    refreshPromise = refreshUnderLock().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function refreshUnderLock(): Promise<{ token: string } | null> {
  if (typeof navigator === 'undefined' || !navigator.locks) return performRefresh();
  return new Promise((resolve, reject) => {
    navigator.locks
      .request(REFRESH_LOCK, async () => {
        try {
          resolve(await performRefresh());
        } catch (error) {
          reject(error);
        }
      })
      .catch(() => reject(new SessionUnavailableError()));
  });
}

async function performRefresh(): Promise<{ token: string } | null> {
  let result: Awaited<ReturnType<typeof refreshAccessToken>>;
  try {
    result = await refreshAccessToken({ credentials: 'include', throwOnError: false });
  } catch {
    throw new SessionUnavailableError();
  }
  if (result.data?.token) {
    useAuthStore.getState().setToken(result.data.token);
    return { token: result.data.token };
  }
  const status = result.response?.status ?? 0;
  if (status === 401 || status === 403) return null;
  throw new SessionUnavailableError();
}
